import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PropertiesPanel } from '@/components/data/PropertiesPanel';
import { BulletListEditor } from '../components/BulletListEditor';
import { SectionCard } from '../components/SectionCard';
import { normalizeDescription } from '../description';
import type { PositionDescription, PositionDetail } from '../types';

export interface DescriptionSectionProps {
  position: PositionDetail;
  canEdit: boolean;
  onPatch: (patch: Record<string, unknown>) => Promise<unknown>;
}

/**
 * Context, responsibilities / must-have / nice-to-have and markdown notes. The API replaces the
 * whole `description` object, so every save sends the merged value.
 */
export function DescriptionSection({ position, canEdit, onPatch }: DescriptionSectionProps) {
  const description = React.useMemo(() => normalizeDescription(position.description), [position.description]);
  const save = React.useCallback(
    (patch: Partial<PositionDescription>) => onPatch({ description: { ...description, ...patch } }),
    [description, onPatch],
  );

  const lists: Array<{ key: 'responsibilities' | 'mustHave' | 'niceToHave'; label: string }> = [
    { key: 'responsibilities', label: 'Responsibilities' },
    { key: 'mustHave', label: 'Must-have' },
    { key: 'niceToHave', label: 'Nice-to-have' },
  ];

  return (
    <SectionCard
      title="Description"
      description="Feeds the JD prefill; keep it factual and free of client names."
    >
      <PropertiesPanel
        columns={1}
        readOnly={!canEdit}
        fields={[
          {
            key: 'context',
            label: 'Context',
            type: 'textarea',
            value: description.context,
            placeholder: 'What the client needs, in a few sentences.',
          },
        ]}
        onSave={(patch) => save({ context: (patch.context as string | null) ?? '' })}
      />
      <div className="grid gap-4 md:grid-cols-3">
        {lists.map(({ key, label }) => (
          <div key={key} className="min-w-0 space-y-1.5">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</h4>
            <BulletListEditor
              label={label}
              value={description[key]}
              onChange={(v) => void save({ [key]: v })}
              readOnly={!canEdit}
              max={20}
            />
          </div>
        ))}
      </div>
      <MarkdownField
        label="Notes"
        value={description.notes}
        onSave={(v) => save({ notes: v })}
        readOnly={!canEdit}
      />
    </SectionCard>
  );
}

function MarkdownField({
  label,
  value,
  onSave,
  readOnly,
}: {
  label: string;
  value: string;
  onSave: (value: string) => Promise<unknown>;
  readOnly: boolean;
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [saving, setSaving] = React.useState(false);
  const id = React.useId();

  const commit = async () => {
    setSaving(true);
    try {
      if (draft !== value) await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <h4 id={`${id}-label`} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label} <span className="font-normal normal-case">(markdown)</span>
        </h4>
        {!readOnly && !editing ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7"
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
          >
            <Pencil aria-hidden="true" /> Edit
          </Button>
        ) : null}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            aria-labelledby={`${id}-label`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void commit();
            }}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => void commit()} loading={saving}>
              Save
            </Button>
          </div>
        </div>
      ) : value.trim() ? (
        <div className="prose prose-sm max-w-none text-foreground dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">–</p>
      )}
    </div>
  );
}
